import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Loader } from '../components/common';
import StorageService from '../services/storage/storageService';
import { generateReports } from '../services/reports/reportEngine';
import ReportBanner from '../components/reports/ReportBanner';
import GaugeChart from '../components/reports/GaugeChart';
import InsightBlock from '../components/reports/InsightBlock';
import ScoreCard from '../components/reports/ScoreCard';
import { useAuth } from '../context/AuthContext';

const ReportPage = () => {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState(null);
  const [activeTab, setActiveTab] = useState(type || 'nutrition'); // nutrition, cognitive, physical

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  useEffect(() => {
    if (type) {
      setActiveTab(type);
    }
  }, [type]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const assessment = await StorageService.getAssessment(id);
      
      if (!assessment) {
        navigate('/');
        return;
      }

      // Generate reports
      const generatedReports = await generateReports(assessment);
      setReports(generatedReports);
      
      // Save reports back to assessment
      await StorageService.updateAssessment(id, {
        reports: generatedReports
      });
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const handleShareReport = async () => {
    const shareUrl = `${window.location.origin}/report/${id}/${activeTab}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader size="lg" text="Generating your reports..." />
      </div>
    );
  }

  if (!reports) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md text-center">
          <p className="text-slate-300 mb-4">No reports found</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </Card>
      </div>
    );
  }

  const { nutritionReport, cognitiveReport, physicalReport } = reports;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      {/* Toast Notification */}
      {showCopiedToast && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">Link copied to clipboard!</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto py-8">
        {/* Header Actions */}
        <div className="flex justify-end items-center mb-6">
          <Button
            variant="primary"
            onClick={handleShareReport}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            }
          >
            Share Report
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('nutrition')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'nutrition'
                ? 'bg-green-600 text-white shadow-lg'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            🥗 Nutrition Report
          </button>
          <button
            onClick={() => setActiveTab('cognitive')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'cognitive'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            🧠 Cognitive Report
          </button>
          <button
            onClick={() => setActiveTab('physical')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'physical'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            🏃 Physical Report
          </button>
        </div>

        {/* Report Content */}
        {activeTab === 'nutrition' && nutritionReport && (
          <div className="space-y-6 animate-fade-in">
            <ReportBanner
              studentName={nutritionReport.studentName}
              school={nutritionReport.school}
              grade={nutritionReport.grade}
              section={nutritionReport.section}
              reportType={nutritionReport.reportType}
              icon="🥗"
              color="green"
            />

            {/* Overall Score */}
            <Card className="text-center">
              <GaugeChart
                score={nutritionReport.score.overall}
                maxScore={100}
                label="Overall Nutrition Score"
                color="green"
                size={250}
              />
              <div className="mt-6">
                <span className={`text-2xl font-bold px-6 py-2 rounded-full ${
                  nutritionReport.score.ratingColor === 'green' ? 'bg-green-500/20 text-green-400' :
                  nutritionReport.score.ratingColor === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                  nutritionReport.score.ratingColor === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {nutritionReport.score.rating}
                </span>
                {nutritionReport.score.badge && (
                  <div className="mt-2 text-sm text-slate-400">
                    Badge: {nutritionReport.score.badge}
                  </div>
                )}
              </div>
            </Card>

            {/* Metabolic Data */}
            {nutritionReport.metabolicData && (
              <Card title="📊 Your Metabolic Profile">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-green-400">{nutritionReport.metabolicData.bmr}</div>
                    <div className="text-sm text-slate-400 mt-1">BMR (kcal/day)</div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-400">{nutritionReport.metabolicData.tdee}</div>
                    <div className="text-sm text-slate-400 mt-1">TDEE (kcal/day)</div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-400">{nutritionReport.metabolicData.proteinTarget}g</div>
                    <div className="text-sm text-slate-400 mt-1">Protein Target</div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-cyan-400">{nutritionReport.metabolicData.hydrationTarget}ml</div>
                    <div className="text-sm text-slate-400 mt-1">Hydration Target</div>
                  </div>
                </div>
              </Card>
            )}

            {/* Nutrition Intake Scores with Descriptions */}
            {nutritionReport.nutritionIntakeScores && (
              <Card title="🎯 Nutrition Intake Scores">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {nutritionReport.nutritionIntakeScores.map((metric, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${
                      metric.status === 'excellent' ? 'bg-slate-800/50 border-slate-600' :
                      metric.status === 'good' ? 'bg-slate-800/40 border-slate-600' :
                      metric.status === 'developing' ? 'bg-slate-800/30 border-slate-700' :
                      'bg-slate-800/20 border-slate-700'
                    }`}>
                      {/* Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{metric.icon}</span>
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${
                            metric.status === 'excellent' ? 'bg-blue-500/20 text-blue-300' :
                            metric.status === 'good' ? 'bg-slate-500/20 text-slate-300' :
                            metric.status === 'developing' ? 'bg-slate-600/20 text-slate-400' :
                            'bg-slate-700/20 text-slate-500'
                          }`}>
                            {metric.status === 'excellent' ? 'Strong Foundation' :
                             metric.status === 'good' ? 'Good' :
                             metric.status === 'developing' ? 'Developing' :
                             'Stagnating'}
                          </span>
                        </div>
                      </div>

                      {/* Title and Score */}
                      <h4 className="text-sm font-semibold text-slate-200 mb-2">{metric.name}</h4>
                      <div className="text-2xl font-bold text-slate-100 mb-2">
                        Score: {metric.score}/{metric.maxScore}
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-slate-700 rounded-full h-2 mb-3">
                        <div
                          className={`h-2 rounded-full ${
                            metric.status === 'excellent' ? 'bg-green-500' :
                            metric.status === 'good' ? 'bg-blue-500' :
                            metric.status === 'developing' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${metric.percentage}%` }}
                        />
                      </div>

                      {/* Description */}
                      <p className="text-xs text-slate-300 leading-relaxed mb-3">
                        {getMetricDescription(metric.name, metric.score, nutritionReport.age, nutritionReport.sport)}
                      </p>

                      {/* Action Tip */}
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <div className="text-xs font-semibold text-blue-400 mb-1">💡 Action Tip:</div>
                        <p className="text-xs text-slate-400">
                          {getMetricActionTip(metric.name, metric.score)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Key Insights Summary */}
            <Card title="📊 Key Nutrition Insights">
              <div className="space-y-4">
                <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                  <h4 className="text-blue-300 font-semibold mb-2">🎯 Overall Assessment</h4>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    Based on your {nutritionReport.age}-year profile ({nutritionReport.metabolicData?.bmr} kcal BMR, {nutritionReport.metabolicData?.tdee} kcal TDEE), 
                    your nutrition score of {nutritionReport.score.overall}% indicates a <strong>{nutritionReport.score.rating.toLowerCase()}</strong> foundation. 
                    {nutritionReport.score.overall >= 80 ? ' You have excellent nutrition habits that support your athletic performance.' :
                     nutritionReport.score.overall >= 65 ? ' You have good habits with room for optimization in key areas.' :
                     ' There are several areas where improvements can significantly boost your performance.'}
                  </p>
                </div>

                {nutritionReport.strengths && nutritionReport.strengths.length > 0 && (
                  <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
                    <h4 className="text-green-300 font-semibold mb-2">💪 Your Top Strengths</h4>
                    <p className="text-slate-300 text-sm leading-relaxed mb-2">
                      You excel in {nutritionReport.strengths.length} key areas:
                    </p>
                    <ul className="space-y-1">
                      {nutritionReport.strengths.slice(0, 3).map((s, i) => (
                        <li key={i} className="text-slate-300 text-sm ml-4">
                          • <strong>{s.category}</strong> ({s.score}%) - Keep up this excellent habit!
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {nutritionReport.areasToImprove && nutritionReport.areasToImprove.length > 0 && (
                  <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                    <h4 className="text-yellow-300 font-semibold mb-2">🎯 Priority Focus Areas</h4>
                    <p className="text-slate-300 text-sm leading-relaxed mb-2">
                      Improving these {nutritionReport.areasToImprove.length} areas will have the biggest impact on your performance:
                    </p>
                    <ul className="space-y-1">
                      {nutritionReport.areasToImprove.slice(0, 3).map((a, i) => (
                        <li key={i} className="text-slate-300 text-sm ml-4">
                          • <strong>{a.category}</strong> ({a.score}%) - Focus here for quick gains
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="p-4 bg-purple-900/20 border border-purple-700/50 rounded-lg">
                  <h4 className="text-purple-300 font-semibold mb-2">⚡ Daily Targets for {nutritionReport.sport}</h4>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-400">{nutritionReport.metabolicData?.calorieTarget}</div>
                      <div className="text-xs text-slate-400">Calories/day</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-400">{nutritionReport.metabolicData?.proteinTarget}g</div>
                      <div className="text-xs text-slate-400">Protein/day</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-cyan-400">{nutritionReport.metabolicData?.hydrationTarget}ml</div>
                      <div className="text-xs text-slate-400">Water/day</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-400">8-9h</div>
                      <div className="text-xs text-slate-400">Sleep/night</div>
                    </div>
                  </div>
                </div>

                {nutritionReport.biohackTip && (
                  <div className="p-4 bg-gradient-to-r from-orange-900/20 to-red-900/20 border border-orange-700/50 rounded-lg">
                    <h4 className="text-orange-300 font-semibold mb-2">{nutritionReport.biohackTip.icon} Weekly Biohack Tip</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">{nutritionReport.biohackTip.tip}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Strengths */}
            {nutritionReport.strengths.length > 0 && (
              <Card title="💪 Your Strengths">
                <div className="space-y-3">
                  {nutritionReport.strengths.map((strength, index) => (
                    <InsightBlock
                      key={index}
                      title={strength.category}
                      icon="✨"
                      points={[strength.message]}
                      type="success"
                    />
                  ))}
                </div>
              </Card>
            )}

            {/* Areas to Improve */}
            {nutritionReport.areasToImprove.length > 0 && (
              <Card title="🎯 Areas to Improve">
                <div className="space-y-4">
                  {nutritionReport.areasToImprove.map((area, index) => (
                    <div key={index} className="space-y-2">
                      <InsightBlock
                        title={area.category}
                        icon="📈"
                        points={[area.message]}
                        type="warning"
                      />
                      <InsightBlock
                        title="Action Steps"
                        icon="✅"
                        points={area.actionSteps}
                        type="tip"
                      />
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Category Breakdown */}
            <Card title="📋 Detailed Category Scores">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {nutritionReport.categoryBreakdown.map((cat, index) => (
                  <ScoreCard
                    key={index}
                    category={cat.category}
                    score={cat.score}
                    rating={cat.rating}
                  />
                ))}
              </div>
            </Card>

            {/* Motivational Message */}
            <InsightBlock
              title="Keep Going!"
              icon="🌟"
              points={[nutritionReport.motivationalMessage]}
              type="success"
            />
          </div>
        )}

        {activeTab === 'cognitive' && cognitiveReport && (
          <div className="space-y-6 animate-fade-in">
            <ReportBanner
              studentName={cognitiveReport.studentName}
              school={cognitiveReport.school}
              grade={cognitiveReport.grade}
              section={cognitiveReport.section}
              reportType={cognitiveReport.reportType}
              icon="🧠"
              color="purple"
            />

            {/* Blueprint Badge */}
            {cognitiveReport.blueprint && (
              <Card className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-purple-500/50">
                <div className="text-center">
                  <div className="text-sm text-purple-300 mb-2">Blueprint</div>
                  <div className="text-3xl font-bold text-white mb-2">{cognitiveReport.blueprint.type}</div>
                  <div className="text-xl text-purple-200 mb-3">{cognitiveReport.blueprint.temperament} Temperament</div>
                  <p className="text-slate-300 text-sm">{cognitiveReport.blueprint.description}</p>
                </div>
              </Card>
            )}

            {/* Dominance Profile */}
            {cognitiveReport.dominanceProfile && (
              <Card title="🎯 Your Dominance Profile">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-3xl mb-2">🧠</div>
                    <div className="text-sm font-medium text-slate-200">Brain</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {cognitiveReport.dominanceProfile.brain.left ? 'Left ✓' : 'Right ✓'}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-3xl mb-2">👁️</div>
                    <div className="text-sm font-medium text-slate-200">Eye</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {cognitiveReport.dominanceProfile.eye.left ? 'Left ✓' : 'Right ✓'}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-3xl mb-2">👂</div>
                    <div className="text-sm font-medium text-slate-200">Ear</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {cognitiveReport.dominanceProfile.ear.left ? 'Left ✓' : 'Right ✓'}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-3xl mb-2">🦵</div>
                    <div className="text-sm font-medium text-slate-200">Leg</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {cognitiveReport.dominanceProfile.leg.left ? 'Left ✓' : 'Right ✓'}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-3xl mb-2">🦶</div>
                    <div className="text-sm font-medium text-slate-200">Foot</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {cognitiveReport.dominanceProfile.foot.left ? 'Left ✓' : 'Right ✓'}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Performance Scores */}
            {cognitiveReport.performanceScores && (
              <Card title="📊 Cognitive Performance Scores">
                <div className="space-y-3">
                  {cognitiveReport.performanceScores.map((score, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-200">{score.domain}</div>
                        <div className="w-full bg-slate-700 rounded-full h-2 mt-1">
                          <div
                            className="h-2 rounded-full bg-purple-500"
                            style={{ width: `${score.score}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-lg font-bold text-slate-200">{score.score}/100</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Trait Analyses */}
            {cognitiveReport.traitAnalyses && cognitiveReport.traitAnalyses.length > 0 && (
              <Card title="🌟 Your Cognitive Traits">
                <div className="space-y-4">
                  {cognitiveReport.traitAnalyses.slice(0, 3).map((trait, index) => (
                    <div key={index} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{trait.icon}</span>
                        <h4 className="text-lg font-semibold text-slate-200">{trait.title}</h4>
                      </div>
                      <p className="text-slate-300 text-sm mb-3">{trait.description}</p>
                      {trait.traits && trait.traits.length > 0 && (
                        <div className="space-y-1">
                          <div className="text-xs font-semibold text-green-400 mb-1">✓ TRAITS:</div>
                          {trait.traits.slice(0, 3).map((t, i) => (
                            <div key={i} className="text-xs text-slate-400 ml-4">• {t}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Ideal Seating */}
            {cognitiveReport.idealSeating && (
              <Card title="💺 Ideal Seating Position">
                <div className="text-center p-4">
                  <div className="text-2xl font-bold text-purple-400 mb-2">
                    {cognitiveReport.idealSeating.position}
                  </div>
                  <p className="text-slate-300 text-sm">{cognitiveReport.idealSeating.description}</p>
                </div>
              </Card>
            )}

            {/* Recommendations */}
            {cognitiveReport.recommendations && cognitiveReport.recommendations.length > 0 && (
              <Card title="💡 Personalized Recommendations">
                <div className="space-y-3">
                  {cognitiveReport.recommendations.map((rec, index) => (
                    <div key={index} className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                      <p className="text-slate-200">{rec}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Learning Style & Career Guidance */}
            {cognitiveReport.learningStyle && (
              <Card title="📚 Your Learning Style">
                <div className="p-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg">
                  <div className="text-xl font-bold text-purple-300 mb-2">
                    {cognitiveReport.learningStyle.type}
                  </div>
                  <p className="text-slate-300">{cognitiveReport.learningStyle.description}</p>
                </div>
              </Card>
            )}

            {/* Career Guidance */}
            {cognitiveReport.careerGuidance && cognitiveReport.careerGuidance.length > 0 && (
              <Card title="🚀 Career & Activity Guidance">
                <div className="space-y-2">
                  {cognitiveReport.careerGuidance.map((guidance, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg">
                      <span className="text-green-400 text-xl">✓</span>
                      <p className="text-slate-300 text-sm">{guidance}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Next Steps */}
            {cognitiveReport.nextSteps && (
              <Card title="🎯 Next Steps" className="bg-gradient-to-r from-green-900/20 to-blue-900/20 border-green-700/50">
                <p className="text-slate-300 leading-relaxed">{cognitiveReport.nextSteps}</p>
              </Card>
            )}

            {/* Dominant Traits (Legacy) */}
            {cognitiveReport.traits && cognitiveReport.traits.length > 0 && (
              <Card title="🌟 Your Personality Traits">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cognitiveReport.traits.map((trait, index) => (
                    <div key={index} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-slate-200 font-semibold">{trait.trait}</h4>
                        <span className="text-primary-400 font-bold">{trait.percentage}%</span>
                      </div>
                      <p className="text-slate-400 text-sm">{trait.description}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Learning Recommendations */}
            {cognitiveReport.learningRecommendations && cognitiveReport.learningRecommendations.length > 0 && (
              <InsightBlock
                title="📚 Learning Recommendations"
                icon="💡"
                points={cognitiveReport.learningRecommendations}
                type="tip"
              />
            )}

            {/* Career Guidance */}
            {cognitiveReport.careerGuidance && cognitiveReport.careerGuidance.length > 0 && (
              <InsightBlock
                title="🎯 Career & Activity Guidance"
                icon="🚀"
                points={cognitiveReport.careerGuidance}
                type="success"
              />
            )}

            {/* Motivational Message */}
            <InsightBlock
              title="Your Unique Profile"
              icon="⭐"
              points={[cognitiveReport.motivationalMessage]}
              type="success"
            />
          </div>
        )}

        {activeTab === 'physical' && physicalReport && (
          <div className="space-y-6 animate-fade-in">
            <ReportBanner
              studentName={physicalReport.studentName}
              school={physicalReport.school}
              grade={physicalReport.grade}
              section={physicalReport.section}
              reportType={physicalReport.reportType}
              icon="🏃"
              color="blue"
            />

            {/* Overall Physical Score */}
            {physicalReport.overallScore && (
              <Card className="text-center">
                <div className="text-6xl mb-4">💪</div>
                <div className="text-4xl font-bold text-blue-400 mb-2">
                  {physicalReport.overallScore.overall}%
                </div>
                <div className="text-xl text-slate-300 mb-2">Overall Physical Score</div>
                <span className={`inline-block px-6 py-2 rounded-full text-lg font-semibold ${
                  physicalReport.overallScore.rating === 'Excellent' ? 'bg-blue-500/20 text-blue-400' :
                  physicalReport.overallScore.rating === 'Good' ? 'bg-slate-500/20 text-slate-300' :
                  physicalReport.overallScore.rating === 'Developing' ? 'bg-slate-600/20 text-slate-400' :
                  'bg-slate-700/20 text-slate-500'
                }`}>
                  {physicalReport.overallScore.rating}
                </span>
                <div className="mt-4 text-sm text-slate-400">
                  {physicalReport.testsCompleted} tests completed
                </div>
              </Card>
            )}

            {/* Component Scores - 4 Fitness Domains */}
            {physicalReport.componentScores && (
              <Card title="🎯 Fitness Components">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(physicalReport.componentScores).map(([key, component]) => (
                    <div key={key} className="text-center p-4 bg-slate-800/30 rounded-lg border border-slate-700">
                      <div className="text-3xl mb-2">{component.icon}</div>
                      <div className="text-lg font-semibold text-slate-200 mb-1">{component.name}</div>
                      <div className="text-2xl font-bold text-blue-400 mb-2">{component.score || 0}%</div>
                      <div className="text-xs text-slate-400">
                        {component.tests && component.tests.length > 0 
                          ? component.tests.join(', ')
                          : 'No tests completed'}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Test Results */}
            {physicalReport.testAnalyses && physicalReport.testAnalyses.length > 0 ? (
              <div className="space-y-6">
                {physicalReport.testAnalyses.map((test, index) => (
                  <Card key={index} title={`${test.testName} - ${test.score}%`}>
                    <div className="space-y-4">
                      {/* Test Header */}
                      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                        <div>
                          <div className="text-2xl font-bold text-blue-400">{test.title}</div>
                          <div className="text-sm text-slate-400 mt-1">
                            {test.reps && `Reps: ${test.reps}`}
                            {test.duration && ` | Duration: ${test.duration}s`}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-slate-200">{test.score}%</div>
                        </div>
                      </div>

                      {/* Areas of Improvement */}
                      {test.areasOfImprovement && test.areasOfImprovement.length > 0 && (
                        <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                          <div className="text-sm font-semibold text-yellow-400 mb-2">⚠️ Areas of Improvement:</div>
                          <ul className="space-y-1">
                            {test.areasOfImprovement.map((area, i) => (
                              <li key={i} className="text-sm text-slate-300 ml-4">• {area}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Suggestions */}
                      {test.suggestions && test.suggestions.length > 0 && (
                        <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                          <div className="text-sm font-semibold text-blue-400 mb-2">💡 Suggestions:</div>
                          <ul className="space-y-1">
                            {test.suggestions.map((suggestion, i) => (
                              <li key={i} className="text-sm text-slate-300 ml-4">• {suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Science & Sport Analysis */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Science Column */}
                        {test.science && (
                          <div className="p-4 bg-cyan-900/20 border border-cyan-700/50 rounded-lg">
                            <div className="text-sm font-semibold text-cyan-400 mb-2">🧪 SCIENCE</div>
                            <p className="text-xs text-slate-300 mb-3">{test.science.explanation}</p>
                            {test.science.toDoFix && (
                              <div className="mt-3">
                                <div className="text-xs font-semibold text-green-400 mb-1">🏃 To-Do Fix:</div>
                                <p className="text-xs text-slate-400">{test.science.toDoFix}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Sport Column */}
                        {test.sport && (
                          <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
                            <div className="text-sm font-semibold text-green-400 mb-2">⚽ SPORT</div>
                            <p className="text-xs text-slate-300 mb-3">{test.sport.application}</p>
                            {test.sport.upgrade14Day && (
                              <div className="mt-3">
                                <div className="text-xs font-semibold text-yellow-400 mb-1">🏃 14-Day Plan:</div>
                                <p className="text-xs text-slate-400">{test.sport.upgrade14Day}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Potential Risks */}
                      {test.potentialRisks && test.potentialRisks.length > 0 && (
                        <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg">
                          <div className="text-sm font-semibold text-red-400 mb-2">⚠️ Potential Risks:</div>
                          <ul className="space-y-1">
                            {test.potentialRisks.map((risk, i) => (
                              <li key={i} className="text-xs text-slate-300 ml-4">• {risk}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <div className="text-center py-12">
                  <div className="text-8xl mb-6">🏃</div>
                  <h3 className="text-2xl font-bold text-slate-100 mb-4">
                    No Physical Tests Completed Yet
                  </h3>
                  <p className="text-slate-400 mb-8">
                    Complete at least 4 physical tests to generate your athletic intelligence report
                  </p>
                  <Button variant="primary" onClick={() => navigate('/physical-tests')}>
                    Start Physical Tests
                  </Button>
                </div>
              </Card>
            )}

            {/* 90-Day Prediction */}
            {physicalReport.prediction90Day && (
              <Card className="bg-gradient-to-r from-blue-900/50 to-green-900/50 border-blue-500/50">
                <div className="text-center p-4">
                  <div className="text-3xl mb-2">{physicalReport.prediction90Day.icon}</div>
                  <div className="text-lg font-semibold text-blue-300 mb-2">90-Day Prediction</div>
                  <p className="text-slate-300 text-sm">{physicalReport.prediction90Day.message}</p>
                </div>
              </Card>
            )}

            {/* Coach's Message */}
            {physicalReport.coachMessage && (
              <Card className="bg-gradient-to-r from-green-900/50 to-blue-900/50 border-green-500/50">
                <div className="text-center p-4">
                  <div className="text-3xl mb-2">{physicalReport.coachMessage.icon}</div>
                  <div className="text-lg font-semibold text-green-300 mb-2">Coach's Message</div>
                  <p className="text-slate-300 text-sm">{physicalReport.coachMessage.message}</p>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to get metric descriptions
function getMetricDescription(metricName, score, age, sport) {
  const descriptions = {
    'Energy Food Quality': score >= 2.5 
      ? `Based on your profile (${age}yrs, ${sport}), your energy intake with complex carbs provides steady fuel. Your BMR and TDEE indicate you're consuming quality carbs like brown rice, atta roti, and oats that sustain energy throughout training.`
      : `Your current energy intake with refined carbs may not support optimal performance. White rice and maida provide quick energy but lack sustained fuel. Current intake may not align with your ${sport} training demands.`,
    'Protein Intake': score >= 2.5
      ? `Your protein target should be approximately 110g per day (2.2g/kg). Current intake supports muscle repair and growth. You're including quality sources like dal, paneer, eggs, and chicken in meals.`
      : `Your protein intake should be approximately 110g per day (2.2g/kg). Current intake may be insufficient for muscle repair and growth. Athletes need more protein than sedentary individuals to support recovery.`,
    'Hydration During Sport': score >= 2.5
      ? `You maintain good hydration with a daily target of 2000ml (40ml/kg). During training, it's crucial to drink 200-300ml every 15-20 minutes to prevent the 20-30% performance drop from dehydration.`
      : `Daily hydration target: 2000ml (40ml/kg). Inadequate hydration during training can significantly reduce performance, increase fatigue, and impair recovery. It's critical for ${sport} performance.`,
    'Post-Workout Recovery': score >= 2.5
      ? `Optimal recovery requires a meal within 30-60 minutes post-training. Your recovery nutrition supports muscle repair and glycogen replenishment with a 3:1 carb:protein ratio.`
      : `The 30-60 minute post-workout window is crucial. Missing this window delays muscle repair and glycogen restoration, leading to prolonged fatigue and reduced adaptation to training.`,
    'Bone Health Foods': score >= 2.5
      ? `During growth years, calcium is critical. Target: 1000-1300mg daily. Good calcium intake supports strong bones and reduces fracture risk, especially important for ${sport}.`
      : `Calcium and vitamin D intake is crucial for growing bones. Target: 1000-1300mg daily. Current intake may not support optimal bone health, increasing injury risk during ${sport}.`,
    'Junk Food Frequency': score >= 2.5
      ? `Limited junk food intake supports better nutrient density, stable energy, and optimal body composition. You're making healthy food choices consistently for ${sport} performance.`
      : `Frequent junk food consumption can impair recovery, increase inflammation, and reduce performance by 10-20%. Empty calories and trans fats lack essential nutrients needed for ${sport}.`,
    'Sleep Quality': score >= 2.5
      ? `Quality sleep (8-10 hours) supports recovery, hormone production, immune function, and mental performance. Your sleep habits optimize adaptation to ${sport} training.`
      : `Sleep is when your body repairs and grows. Athletes need 8-10 hours. Inadequate sleep (<7 hours) reduces performance by 20-30%, impairs reaction time, and increases injury risk by 70%.`,
    'Meal Frequency': score >= 2.5
      ? `Consistent meal timing maintains stable blood sugar, steady energy, and optimal nutrient delivery. Your eating pattern supports ${sport} training and recovery needs.`
      : `Irregular eating patterns cause energy crashes and poor focus. Long gaps (>4-5 hours) can lead to muscle breakdown. Consistent timing is crucial for ${sport} performance.`,
    'Micronutrient Balance': score >= 2.5
      ? `Diverse nutrient intake from colorful foods supports all physiological systems. Your vitamin and mineral intake supports energy production, immunity, and recovery for ${sport}.`
      : `Vitamins and minerals are essential for energy, immunity, and recovery. Inadequate micronutrients can cause fatigue, frequent illness, and poor recovery despite adequate macros.`
  };
  return descriptions[metricName] || `Score indicates ${score >= 2.5 ? 'good' : 'developing'} habits in this area.`;
}

// Helper function to get action tips
function getMetricActionTip(metricName, score) {
  if (score >= 2.5) {
    const goodTips = {
      'Energy Food Quality': 'Maintain your excellent carb choices. Time your largest carb meals 2-3 hours before training.',
      'Protein Intake': 'Continue post-workout protein within 30 min. Add Greek yogurt or cottage cheese before bed for overnight recovery.',
      'Hydration During Sport': 'Maintain your protocol. Weigh before/after training to track fluid loss. Add electrolytes for intense sessions.',
      'Post-Workout Recovery': 'Perfect timing! Optimize with liquid options for faster absorption like chocolate milk, smoothies, or protein shakes.',
      'Bone Health Foods': 'Maintain 2-3 dairy servings daily. Pair with Vitamin D. Get 15-20 min sunlight daily for optimal absorption.',
      'Junk Food Frequency': 'Great discipline! Maintain your 80/20 approach. When indulging, time it post-workout and focus on portion control.',
      'Sleep Quality': 'Excellent habits! Maintain 8-10 hour schedule. Track sleep quality. Consider magnesium (400mg) for deeper sleep.',
      'Meal Frequency': 'Great timing! Maintain 4-6 meals daily, every 3-4 hours. Optimize with largest meal 2-3h pre-training, recovery within 30 min.',
      'Micronutrient Balance': 'Excellent diversity! Ensure adequate Iron, Vitamin D, Magnesium, Antioxidants. Consider blood work every 6-12 months.'
    };
    return goodTips[metricName] || 'Keep up the excellent work!';
  } else {
    const improveTips = {
      'Energy Food Quality': 'Switch to whole grains like brown rice, atta roti, oats. Replace maida with whole wheat. Time carbs 2-3h pre-training.',
      'Protein Intake': 'Increase to 110g daily. Include protein in every meal like dal, paneer, eggs, chicken. Post-workout needs 20-25g within 30 min.',
      'Hydration During Sport': 'Pre-training drink 400-600ml 2-3h before. During training drink 200-300ml every 15-20 min. Post-training drink 150% of fluid lost. Monitor urine color.',
      'Post-Workout Recovery': 'Eat within 30-60 min with 3 to 1 carb protein ratio. Options include chocolate milk, banana with whey, 2 eggs with toast, paneer sandwich.',
      'Bone Health Foods': 'Increase calcium intake with milk (300mg per cup), dahi, paneer (200mg per 100g), ragi (350mg per 100g). Target 2-3 servings daily.',
      'Junk Food Frequency': 'Limit to once weekly. Healthier swaps include baked vs fried, fruit vs chips. Plan ahead with healthy snacks like nuts and fruits.',
      'Sleep Quality': 'Aim for 8-10 hours. Keep consistent schedule, cool room (18-20C), no screens 1h before bed. Pre-sleep eat casein protein.',
      'Meal Frequency': 'Eat every 3-4 hours with Breakfast (7-8 AM), Snack (10-11 AM), Lunch (12-1 PM), Snack (4-5 PM), Dinner (7-8 PM).',
      'Micronutrient Balance': 'Eat the rainbow with 5+ colors daily. Focus on Iron (spinach, meat), Vitamin C (citrus), B-vitamins (whole grains, eggs).'
    };
    return improveTips[metricName] || 'Focus on consistent improvement in this area.';
  }
}

export default ReportPage;
